import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import InputAdornment from '@mui/material/InputAdornment';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Search } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { get } from '../../api/client';
import { usePermission } from '../../auth/permissions';
import { DataTable, type Column } from '../../components/DataTable';
import { useDialog, type DialogProps } from '../../components/dialogs/DialogProvider';
import { EmptyState } from '../../components/EmptyState';
import { PageHeader } from '../../components/PageHeader';
import { formatAgo, formatDateTime } from '../../lib/format';
import { colours, radius } from '../../theme';
import { actionLabel, AREA_LABELS, changesOf, fieldLabel, formatValue, type Change } from './describe';

export interface AuditEntry {
  id: string;
  createdAt: string;
  action: string;
  entityType: string | null;
  entityId: string | null;
  entityLabel: string | null;
  before: unknown;
  after: unknown;
  ipAddress: string | null;
  userId: string | null;
  userName: string | null;
  userEmail: string | null;
}

const PAGE_SIZE = 50;
const RANGES = [
  { value: '1', label: 'Last 24 hours' },
  { value: '7', label: 'Last 7 days' },
  { value: '30', label: 'Last 30 days' },
  { value: '90', label: 'Last 90 days' },
  { value: 'all', label: 'Everything' },
  { value: 'custom', label: 'Choose dates' },
] as const;

/** yyyy-mm-dd in the browser's time zone, `daysAgo` days back. */
const localDay = (daysAgo = 0) => {
  const d = new Date();
  d.setDate(d.getDate() - daysAgo);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

/** Admins: who did what in Pulsepoint, what it changed, and when. Newest first. */
export function AuditPage() {
  const canView = usePermission('audit.view');
  const dialog = useDialog();
  const [range, setRange] = useState<(typeof RANGES)[number]['value']>('30');
  const [userId, setUserId] = useState('');
  const [action, setAction] = useState('');
  const [typed, setTyped] = useState('');
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);

  // Search as you type, a moment after you stop.
  useEffect(() => {
    const t = setTimeout(() => setQ(typed.trim()), 350);
    return () => clearTimeout(t);
  }, [typed]);
  // Choose dates: whole days in the browser's time zone, both included.
  const [days, setDays] = useState({ from: localDay(30), to: localDay() });
  const badDates = range === 'custom' && (!days.from || !days.to || days.from > days.to);
  useEffect(() => setPage(1), [range, userId, action, q, days]);

  const search = useMemo(() => {
    const p = new URLSearchParams({ page: String(page), pageSize: String(PAGE_SIZE) });
    // Rounded to the minute, so the query (and its cache) doesn't change every render.
    if (range === 'custom') {
      if (days.from) p.set('from', new Date(`${days.from}T00:00:00`).toISOString());
      if (days.to) {
        const end = new Date(`${days.to}T00:00:00`);
        end.setDate(end.getDate() + 1); // to the end of that day
        p.set('to', end.toISOString());
      }
    } else if (range !== 'all') p.set('from', new Date(Math.floor((Date.now() - Number(range) * 86_400_000) / 60_000) * 60_000).toISOString());
    if (userId) p.set('userId', userId);
    if (action) p.set('action', action);
    if (q) p.set('q', q);
    return p.toString();
  }, [range, days, userId, action, q, page]);

  const options = useQuery({ queryKey: ['audit', 'options'], queryFn: () => get<{ users: { id: string; name: string; email: string }[]; actions: string[] }>('/audit/options'), enabled: canView });
  const list = useQuery({ queryKey: ['audit', search], queryFn: () => get<{ rows: AuditEntry[]; total: number }>(`/audit?${search}`), placeholderData: keepPreviousData, enabled: canView && !badDates });

  if (!canView) return <EmptyState title="Admins only">The audit log is only available to admins.</EmptyState>;

  const columns: Column<AuditEntry>[] = [
    {
      key: 'when',
      header: 'When',
      render: (e) => (
        <Box>
          <Typography className="num" sx={{ whiteSpace: 'nowrap' }}>{formatDateTime(e.createdAt)}</Typography>
          <Typography variant="body2" color="textSecondary">{formatAgo(e.createdAt)}</Typography>
        </Box>
      ),
    },
    {
      key: 'who',
      header: 'Who',
      render: (e) => (
        <Box>
          <Typography sx={{ fontWeight: 500 }}>{e.userName ?? (e.action.startsWith('driver.signUp') ? 'A driver (sign-up link)' : 'Pulsepoint')}</Typography>
          {e.userEmail && <Typography variant="body2" color="textSecondary">{e.userEmail}</Typography>}
        </Box>
      ),
    },
    {
      key: 'what',
      header: 'What',
      render: (e) => (
        <Box>
          <Typography>{actionLabel(e.action)}</Typography>
          {(e.entityLabel || e.entityType) && <Typography variant="body2" color="textSecondary">{e.entityLabel ?? 'No longer exists'}</Typography>}
        </Box>
      ),
    },
    {
      key: 'changes',
      header: 'Changes',
      hideBelow: 'md',
      render: (e) => {
        const c = changesOf(e.before, e.after);
        if (!c.length) return <Typography color="textSecondary">—</Typography>;
        const shown = c.slice(0, 3).map((x) => fieldLabel(x.field)).join(', ');
        return <Typography color="textSecondary">{c.length > 3 ? `${shown} and ${c.length - 3} more` : shown}</Typography>;
      },
    },
  ];

  return (
    <>
      <PageHeader title="Audit log." lead="Who did what in Pulsepoint, what it changed, and when. Click an entry to see the changes." />
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} sx={{ mb: 3, flexWrap: 'wrap', rowGap: 1.5 }}>
        <TextField select size="small" value={range} onChange={(e) => setRange(e.target.value as typeof range)} sx={{ width: { xs: '100%', md: 180 } }} slotProps={{ htmlInput: { 'aria-label': 'When' } }}>
          {RANGES.map((r) => <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>)}
        </TextField>
        {range === 'custom' && (
          <Stack direction="row" spacing={1}>
            <TextField size="small" type="date" label="From" value={days.from} onChange={(e) => setDays({ ...days, from: e.target.value })} slotProps={{ inputLabel: { shrink: true }, htmlInput: { max: days.to || localDay() } }} />
            <TextField
              size="small"
              type="date"
              label="To"
              value={days.to}
              onChange={(e) => setDays({ ...days, to: e.target.value })}
              error={badDates}
              helperText={badDates ? 'Before the start' : undefined}
              slotProps={{ inputLabel: { shrink: true }, htmlInput: { min: days.from, max: localDay() } }}
            />
          </Stack>
        )}
        <TextField select size="small" value={userId} onChange={(e) => setUserId(e.target.value)} sx={{ width: { xs: '100%', md: 220 } }} slotProps={{ htmlInput: { 'aria-label': 'Who' }, select: { displayEmpty: true } }}>
          <MenuItem value="">Everyone</MenuItem>
          {(options.data?.users ?? []).map((u) => <MenuItem key={u.id} value={u.id}>{u.name}</MenuItem>)}
        </TextField>
        <TextField select size="small" value={action} onChange={(e) => setAction(e.target.value)} sx={{ width: { xs: '100%', md: 220 } }} slotProps={{ htmlInput: { 'aria-label': 'What' }, select: { displayEmpty: true } }}>
          <MenuItem value="">Everything</MenuItem>
          {(options.data?.actions ?? []).map((a) => <MenuItem key={a} value={a}>{AREA_LABELS[a] ?? a}</MenuItem>)}
        </TextField>
        <TextField
          size="small"
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
          placeholder="Search names, items, values"
          sx={{ flex: 1, minWidth: { md: 220 } }}
          slotProps={{ htmlInput: { 'aria-label': 'Search' }, input: { startAdornment: <InputAdornment position="start"><Search size={18} strokeWidth={1.75} /></InputAdornment> } }}
        />
      </Stack>
      <DataTable
        rows={list.data?.rows}
        columns={columns}
        rowKey={(e) => e.id}
        loading={list.isPending}
        error={list.error}
        onRetry={() => void list.refetch()}
        onRowClick={(e) => void dialog.open(AuditEntryDialog, { entry: e })}
        empty={<EmptyState title="Nothing recorded">Try a longer time, or clear the filters.</EmptyState>}
        label="Audit log"
        server={{ total: list.data?.total ?? 0, page, pageSize: PAGE_SIZE, onPage: setPage, sort: { key: 'when', direction: 'desc' }, onSort: () => undefined }}
      />
    </>
  );
}

/** One entry: who, when, from where, and each field before and after. */
function AuditEntryDialog({ entry: e, close }: { entry: AuditEntry } & DialogProps) {
  const changes = changesOf(e.before, e.after);
  const created = e.before == null && e.after != null;
  const removed = e.after == null && e.before != null;
  return (
    <Dialog open onClose={() => close()} fullWidth maxWidth="md" scroll="paper" aria-labelledby="audit-entry-title">
      <DialogTitle id="audit-entry-title">{actionLabel(e.action)}</DialogTitle>
      {/* Ordinary block layout, so long changes make the content scroll rather than squeeze. */}
      <DialogContent dividers sx={{ overflowY: 'auto' }}>
        <Box component="dl" sx={{ m: 0, mb: 2.5, display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '140px 1fr' }, rowGap: 1, columnGap: 2 }}>
          <Meta k="When" v={`${formatDateTime(e.createdAt)} (${formatAgo(e.createdAt)})`} />
          <Meta k="Who" v={e.userName ? `${e.userName}${e.userEmail ? ` · ${e.userEmail}` : ''}` : 'Pulsepoint, or someone not signed in'} />
          {(e.entityLabel || e.entityType) && <Meta k="Item" v={[e.entityLabel ?? 'No longer exists', e.entityType ? `(${e.entityType})` : null].filter(Boolean).join(' ')} />}
          {e.ipAddress && <Meta k="From" v={e.ipAddress} />}
          <Meta k="Recorded as" v={e.action} />
        </Box>
        {changes.length > 0 ? (
          <Box sx={{ border: `1px solid ${colours.hairline}`, borderRadius: `${radius.panel}px` }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: created || removed ? '200px 1fr' : '200px 1fr 1fr', gap: 2, bgcolor: colours.paleGrey, px: 2, py: 1, borderRadius: `${radius.panel}px ${radius.panel}px 0 0` }}>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>Field</Typography>
              {!created && <Typography variant="body2" sx={{ fontWeight: 500 }}>{removed ? 'Value' : 'Before'}</Typography>}
              {!removed && <Typography variant="body2" sx={{ fontWeight: 500 }}>{created ? 'Value' : 'After'}</Typography>}
            </Box>
            {changes.map((c: Change) => (
              <Box key={c.field} sx={{ display: 'grid', gridTemplateColumns: created || removed ? '200px 1fr' : '200px 1fr 1fr', px: 2, py: 1, borderTop: `1px solid ${colours.hairline}`, gap: 2 }}>
                <Typography variant="body2" color="textSecondary">{fieldLabel(c.field)}</Typography>
                {!created && <Value v={c.before} struck={!removed} />}
                {!removed && <Value v={c.after} />}
              </Box>
            ))}
          </Box>
        ) : (
          <Typography color="textSecondary">No field changes were recorded for this.</Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => close()}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

function Meta({ k, v }: { k: string; v: string }) {
  return (
    <>
      <Typography component="dt" variant="body2" color="textSecondary">{k}</Typography>
      <Typography component="dd" sx={{ m: 0, wordBreak: 'break-word' }}>{v}</Typography>
    </>
  );
}

function Value({ v, struck }: { v: unknown; struck?: boolean }) {
  return (
    <Typography variant="body2" sx={{ wordBreak: 'break-word', whiteSpace: 'pre-wrap', color: struck ? colours.graphite : undefined }}>
      {formatValue(v)}
    </Typography>
  );
}
